/**
 * Mounts the baked wordmark (logo-art.js) into the logo boxes of index.html:
 *   <div data-toon-logo="stacked|line" role="img" aria-label="…"><canvas class="toon-logo__art"></canvas></div>
 *
 * The box keeps the old wordmark's layout size (cartoon-logo.css), so the columns around it do
 * not move; the canvas bleeds a little past it. Each canvas is painted from the bake cache at
 * its CSS size × devicePixelRatio, and repainted only when that pixel size changes (layout
 * resize, zoom, DPR change) or when the logo font arrives after a fallback bake.
 */
import { getToonLogoCanvas, isToonLogoFontReady, whenToonLogoFontReady } from "./logo-art.js";

/** @type {{ node: Element, canvas: HTMLCanvasElement, variant: string, key: string }[]} */
var entries = [];
var observer = null;
var scheduled = false;
var windowHooked = false;
var lateFontHooked = false;
var fontGate = null;

function cssSize(el) {
  try {
    var cs = getComputedStyle(el);
    if (cs.display === "none") return null;
    var w = parseFloat(cs.width), h = parseFloat(cs.height);
    return w > 0 && h > 0 ? { w: w, h: h } : null;
  } catch (_e) {
    return null;
  }
}

function paint(entry) {
  var size = cssSize(entry.canvas);
  if (!size) return false; // hidden (other layout): the ResizeObserver paints it when shown
  var dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
  var w = Math.max(1, Math.round(size.w * dpr));
  var h = Math.max(1, Math.round(size.h * dpr));
  var key = entry.variant + "|" + w + "x" + h + "|" + (isToonLogoFontReady() ? "f" : "x");
  if (key === entry.key) return true;
  var art = getToonLogoCanvas(entry.variant, w, h);
  var c = art && entry.canvas.getContext ? entry.canvas.getContext("2d") : null;
  if (!c) return false;
  if (entry.canvas.width !== w) entry.canvas.width = w;
  if (entry.canvas.height !== h) entry.canvas.height = h;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, w, h);
  c.drawImage(art, 0, 0);
  entry.key = key;
  entry.node.classList.add("toon-logo--ready");
  return true;
}

function paintAll() {
  scheduled = false;
  for (var i = 0; i < entries.length; i++) paint(entries[i]);
  if (!lateFontHooked && !isToonLogoFontReady()) hookLateFont();
}

function schedule() {
  if (scheduled || fontGate) return;
  scheduled = true;
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(paintAll);
  else setTimeout(paintAll, 0);
}

/** The font missed the first bake (timeout): re-bake once when it finally loads. */
function hookLateFont() {
  if (typeof document === "undefined" || !document.fonts || !document.fonts.addEventListener) return;
  lateFontHooked = true;
  var onDone = function () {
    if (!isToonLogoFontReady()) return;
    document.fonts.removeEventListener("loadingdone", onDone);
    schedule();
  };
  document.fonts.addEventListener("loadingdone", onDone);
}

function watchDpr() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
  var mq = window.matchMedia("(resolution: " + (window.devicePixelRatio || 1) + "dppx)");
  var onChange = function () {
    if (mq.removeEventListener) mq.removeEventListener("change", onChange);
    else if (mq.removeListener) mq.removeListener(onChange);
    schedule();
    watchDpr();
  };
  if (mq.addEventListener) mq.addEventListener("change", onChange);
  else if (mq.addListener) mq.addListener(onChange);
}

/**
 * Find every `[data-toon-logo]` box under `root` (default: document) and keep its canvas
 * painted. Safe to call again (already-mounted boxes are skipped) and a no-op without a DOM.
 * @param {ParentNode} [root]
 * @returns {number} how many boxes are now mounted
 */
export function mountToonLogos(root) {
  if (typeof document === "undefined") return 0;
  var scope = root || document;
  var nodes = scope.querySelectorAll ? scope.querySelectorAll("[data-toon-logo]") : [];
  for (var i = 0; i < nodes.length; i++) {
    var node = nodes[i];
    if (node.__toonLogo) continue;
    var canvas = node.querySelector("canvas.toon-logo__art");
    if (!canvas) continue;
    var entry = {
      node: node,
      canvas: canvas,
      variant: node.getAttribute("data-toon-logo") === "line" ? "line" : "stacked",
      key: "",
    };
    node.__toonLogo = entry;
    entries.push(entry);
    if (typeof ResizeObserver === "function") {
      if (!observer) observer = new ResizeObserver(schedule);
      observer.observe(canvas);
    }
  }
  if (!windowHooked && typeof window !== "undefined" && window.addEventListener) {
    windowHooked = true;
    window.addEventListener("resize", schedule);
    watchDpr();
  }
  if (isToonLogoFontReady()) {
    schedule();
  } else if (!fontGate) {
    // First bake waits for the logo font (bounded), so the fallback face never flashes.
    fontGate = whenToonLogoFontReady().then(function () {
      fontGate = null;
      schedule();
    });
  }
  return entries.length;
}

/** @internal Test helper. */
export function __resetToonLogoMountsForTests() {
  if (observer) observer.disconnect();
  for (var i = 0; i < entries.length; i++) delete entries[i].node.__toonLogo;
  entries = [];
  observer = null;
  scheduled = false;
  fontGate = null;
  lateFontHooked = false;
}
